import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { LetterFlies } from 'amira-intervention-library';
import useFetchWordMetadata from '../../hooks/useFetchWordMetadata'
import useReadingMode from '../../hooks/useReadingMode';
import useEndingSpeech from '../../hooks/useEndingSpeech';
import useMaxListenTimer from '../../hooks/useMaxListenTimer';
import { COPY_STRINGS } from '../../../../../constants/translatedCopy';
import get from 'lodash.get';
import usePauseControl from '../../hooks/usePauseControl';
import useDidAttempt from '../../hooks/useDidAttempt';
import { logSentryException } from '../../../../../services/util';
export default function LetterFliesWrapper({
  speech,
  startPhrase,
  isFTUE = true,
  currentWordIndex,
  phraseMetadata,
  isInstruction,
  transcript,
  paused,
  setWillHandlePause,
  pausePhrase,
  resumePhrase,
  doneHandlingPause
}) {

  const { type, targetGrapheme, word, instructions } = phraseMetadata
  const targetIndex = word.indexOf(targetGrapheme)

  const { shouldHandlePause, finishHandlePause, passPauseControlBackToReaderManager } = usePauseControl({
    paused,
    setWillHandlePause,
    pausePhrase,
    resumePhrase,
    doneHandlingPause,
    transcript
  })

  const { didAttempt } = useDidAttempt({transcript})

  const { replaceTheEndingSequence } = useEndingSpeech()
  const { maxListenTimerEnded, startMaxListenTimer } = useMaxListenTimer()

  useEffect(() => {
    speech.stopListening();
  }, [])

  const { wordMetadata, error, loading } = useFetchWordMetadata({ word })
  const { wordMetadata: exampleWordMetadata, loading: exampleLoading, error: exampleError} = useFetchWordMetadata({ word: get(instructions, "word") })

  /**
   * provide end of phrase feedback on correct, incorrect and silence
   */
  useEffect(() => {
    // if word index moved before the listen timer ends, then it is correct.
    if(didAttempt){
      if(!maxListenTimerEnded && currentWordIndex > 0){
        // correct
        replaceTheEndingSequence([{
          textToSpeak: COPY_STRINGS.intervention_strings.letter_flies.correct_2,
        }]);
      }else{
        // incorrect
        replaceTheEndingSequence([{
          textToSpeak: COPY_STRINGS.intervention_strings.letter_flies.tutor.struggle,
        }]);
      }
    }

    return () => {
      if(!didAttempt && currentWordIndex === 0){
        // silence
        replaceTheEndingSequence(getSilentFeedbackSpeech());
      }
    }

  // only run this effect when the word index changes because we only want to 
  // calculate the feedback to give when the word is finished.
  }, [didAttempt, currentWordIndex])

  /**
   * Gives the sound: [targetGrapheme] makes the [phonemeToSpeak] sound.
   * @returns {Array} - array of objects that contain the textToSpeak and phonemeToSpeak
   */
  const getSilentFeedbackSpeech = () => {
    return [
      {textToSpeak: COPY_STRINGS.intervention_strings.letter_flies.tutor.silent_letter({targetGrapheme}),},
      {textToSpeak: COPY_STRINGS.intervention_strings.letter_flies.tutor.silent_instruction_3,},
      {phonemeToSpeak: instructions.targetPhoneme,},
      {textToSpeak: COPY_STRINGS.intervention_strings.letter_flies.tutor.silent_instruction_4,},
    ];
  }

  const { isTutorMode } = useReadingMode()

  const missingWordMetadata = () => {
    return (!loading && wordMetadata && (!wordMetadata.PHON || !wordMetadata.GRAPHEMIC_BREAKDOWN))
  }
  const missingExampleWordMetadata = () => {
    return (!exampleLoading && exampleWordMetadata && (!exampleWordMetadata.PHON || !exampleWordMetadata.GRAPHEMIC_BREAKDOWN))
  }

  useEffect(() => {
    // Prevent a blank screen crash by checking for the minium required data to load the activity
    if(missingWordMetadata()){
      console.error("word metadata missing for " + word, wordMetadata)
      logSentryException(new Error("word metadata missing for " + word));
    }
    if(missingExampleWordMetadata()){
      console.error("word metadata missing for " + instructions.word, exampleWordMetadata)
      logSentryException(new Error("word metadata missing for " + instructions.word));
    }
  }, [loading, exampleLoading])

  if (loading || exampleLoading) {
    return <div></div>
  }

  if (error || exampleError || missingWordMetadata() || missingExampleWordMetadata()) {
    return <div>Error loading some data, please refresh and retry.</div>
  }

  const example = instructions && instructions.word && instructions.targetPhoneme && instructions.text && exampleWordMetadata ? {
    word: instructions.word,
    phon: instructions.targetPhoneme,
    targetGrapheme: instructions.text,
    GRAPHEMIC_BREAKDOWN: exampleWordMetadata.GRAPHEMIC_BREAKDOWN,
  }: null

  const metadata = {
    isInstruction,
    speech,
    word,
    paused,
    tutorMode: isTutorMode,
    ftue: isFTUE,
    wordMetadata: {
      PHON: wordMetadata.PHON,
      GRAPHEMIC_BREAKDOWN: wordMetadata.GRAPHEMIC_BREAKDOWN,
      targetGrapheme,
      example,
    },
    handleMessage: (message) => { // basically gives it the full control of the phrase timing
        const { event } = message
        if (event === "start") {
          startPhrase()
          startMaxListenTimer()
        }else if (event === "retry") {
          startMaxListenTimer()
          finishHandlePause()
          passPauseControlBackToReaderManager()
      }
    },
  }

  const states = {
    paused: paused && shouldHandlePause,
  }

  return <LetterFlies {...metadata} {...states} />
}

LetterFlies.propTypes = {
  speech: PropTypes.object,
  startPhrase: PropTypes.func,
  isFTUE: PropTypes.bool,
  currentWordIndex: PropTypes.number,
  phraseMetadata: PropTypes.object,
}